import { WORKFLOW_CHANNELS, type WorkflowStepType } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, ErrorState, Field, Icon, Input, Segmented, Select, Skeleton, type IconName } from "@sagot/ui";
import { useState, type SyntheticEvent } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useRefs, type BuilderRefs } from "../builder/refs";
import { STEP_ICONS } from "../builder/step-icons";
import { summarize } from "../builder/step-summary";
import type { Definition, DryRunResult, DryRunStep } from "../types";
import { groupByPath, isMismatch, stepsById } from "./preview-model";
import { useDryRun } from "./use-preview";

const P = "engage.workflows.builder.preview.dry";
const MESSAGE_MAX = 500;
type Channel = (typeof WORKFLOW_CHANNELS)[number];
type TFunction = ReturnType<typeof useTranslation>["t"];

const text = (value: unknown): string => (typeof value === "string" ? value : "");

function isChannel(value: string | undefined): value is Channel {
  return WORKFLOW_CHANNELS.some((channel) => channel === value);
}

/** The step in one line (its summary, with the rule it came from). */
function summaryLine(planned: DryRunStep, definition: Definition, refs: BuilderRefs, t: TFunction): string {
  const step = stepsById(definition).get(planned.step_id);
  const summary = step ? summarize(step, refs, t) : "";
  return planned.detail.rule ? `${summary} (${t(`${P}.inRule`, { rule: text(planned.detail.rule) })})` : summary;
}

/** What a planned step did, in words: the branch taken, the rules that matched, where it went. */
function describe(planned: DryRunStep, definition: Definition, refs: BuilderRefs, t: TFunction): string {
  const detail = planned.detail;
  switch (planned.type) {
    case "branch":
      return text(detail.branch) === "else" ? t(`${P}.branchElse`) : t(`${P}.branch`, { label: text(detail.branch) });
    case "apply_rules": {
      const rules = Array.isArray(detail.rules) ? detail.rules.map(text).filter(Boolean) : [];
      return rules.length > 0 ? t(`${P}.rules`, { rules: rules.join(", ") }) : t(`${P}.rulesNone`);
    }
    case "go_to_path":
      return t(`${P}.goTo`, { path: text(detail.to) });
    case "end":
      return t(`${P}.ended`);
    case "snooze": {
      const skipped = text(detail.skipped);
      if (skipped) return t(`${P}.snoozeSkipped`, { reason: t(`${P}.snoozeSkips.${skipped}`, { defaultValue: skipped }) });
      return t(`${P}.snoozedUntil`, { time: text(detail.snoozed_until) });
    }
    case "wait": {
      // A wait until the office opens either finds the office open (skipped) or ends at its next opening.
      const skipped = text(detail.skipped);
      if (skipped) return t(`${P}.waitSkipped`, { reason: t(`${P}.snoozeSkips.${skipped}`, { defaultValue: skipped }) });
      return detail.wakes_at ? t(`${P}.waitsUntil`, { time: text(detail.wakes_at) }) : summaryLine(planned, definition, refs, t);
    }
    default:
      return summaryLine(planned, definition, refs, t);
  }
}

function StepLine({ planned, line }: { planned: DryRunStep; line: string }) {
  const { t } = useTranslation();
  const icon: IconName = planned.type in STEP_ICONS ? STEP_ICONS[planned.type as WorkflowStepType] : "circle";
  return (
    <li className="flex items-start gap-3 rounded-md border border-line bg-surface p-3" data-testid="dry-run-step">
      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-sunken text-primary-ink">
        <Icon name={icon} className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-bold uppercase tracking-wider text-fg-subtle">{t(`engage.workflows.steps.${planned.type}`, { defaultValue: planned.type })}</span>
        <span className="block break-words text-sm">{line}</span>
      </span>
    </li>
  );
}

function Verdict({ result }: { result: DryRunResult }) {
  const { t } = useTranslation();
  const reason = isMismatch(result.mismatch) ? t(`${P}.mismatch.${result.mismatch}`) : null;
  return (
    <div className={result.runs ? "rounded-md bg-success-soft p-3 text-sm" : "rounded-md bg-warning-soft p-3 text-sm"} data-testid="dry-run-verdict">
      <p className="flex items-center gap-2 font-semibold">
        <Icon name={result.runs ? "check-circle" : "alert-triangle"} className="size-4" />
        {result.runs ? t(`${P}.runs`) : t(`${P}.wouldNot`)}
      </p>
      {reason ? <p className="mt-1 text-xs text-fg-muted">{reason}</p> : null}
      {result.paths.length > 0 ? <p className="mt-1 text-xs text-fg-muted">{t(`${P}.paths`, { paths: result.paths.join(" > ") })}</p> : null}
    </div>
  );
}

function Result({ result, definition }: { result: DryRunResult; definition: Definition }) {
  const { t } = useTranslation();
  const refs = useRefs();
  const groups = groupByPath(result.steps);
  return (
    <div className="space-y-4" aria-live="polite">
      <Verdict result={result} />
      <h3 className="text-sm font-bold">{t(`${P}.steps`)}</h3>
      {groups.length === 0 ? <p className="text-sm text-fg-muted">{t(`${P}.empty`)}</p> : null}
      {groups.map((group) => {
        const label = definition.paths[group.path]?.label;
        const heading = label ? t(`${P}.pathHeadingNamed`, { id: group.path, label }) : t(`${P}.pathHeading`, { id: group.path });
        return (
          <section key={group.path} aria-label={heading} className="space-y-2">
            <h4 className="text-xs font-bold uppercase tracking-wider text-fg-subtle">{heading}</h4>
            <ol className="space-y-2">
              {group.steps.map((planned) => <StepLine key={planned.step_id} planned={planned} line={describe(planned, definition, refs, t)} />)}
            </ol>
          </section>
        );
      })}
      {result.too_long ? <p className="text-xs text-warning">{t(`${P}.tooLong`)}</p> : null}
    </div>
  );
}

interface DryRunPanelProps {
  workflowId: string;
  definition: Definition;
}

/** Background workflows: pick sample facts, see which steps would run, on which path. Nothing executes. */
export function DryRunPanel({ workflowId, definition }: DryRunPanelProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const dry = useDryRun(workflowId);
  const firstChannel = definition.settings.channels[0];
  const [channel, setChannel] = useState<Channel>(isChannel(firstChannel) ? firstChannel : "widget");
  const [officeHours, setOfficeHours] = useState(true);
  const [sample, setSample] = useState("");
  const run = (event?: SyntheticEvent) => {
    event?.preventDefault();
    dry.mutate({ definition, channel, officeHours, message: sample });
  };
  return (
    <div className="space-y-5">
      <form className="space-y-4" onSubmit={run}>
        <p className="text-sm font-bold">{t(`${P}.facts`)}</p>
        <Field label={t(`${P}.channel`)}>
          <Select value={channel} onChange={(e) => { setChannel(e.target.value as Channel); }}>
            {WORKFLOW_CHANNELS.map((key) => <option key={key} value={key}>{t(`engage.channels.${key}`)}</option>)}
          </Select>
        </Field>
        <div>
          <span className="label">{t(`${P}.officeHours`)}</span>
          <Segmented
            aria-label={t(`${P}.officeHours`)}
            options={[{ value: "open", label: t(`${P}.open`) }, { value: "closed", label: t(`${P}.closed`) }]}
            value={officeHours ? "open" : "closed"}
            onValueChange={(value) => { setOfficeHours(value === "open"); }}
          />
        </div>
        <Field label={t(`${P}.message`)} hint={t(`${P}.messageHint`)}>
          <Input maxLength={MESSAGE_MAX} value={sample} onChange={(e) => { setSample(e.target.value); }} />
        </Field>
        <Button type="submit" loading={dry.isPending}>
          <Icon name="play" />
          {t(`${P}.run`)}
        </Button>
      </form>
      {dry.isError ? <ErrorState headingLevel={3} title={t(`${P}.failedTitle`)} description={message(dry.error)} onRetry={() => { run(); }} retryLabel={t("common.retry")} /> : null}
      {dry.isPending ? <Skeleton className="h-24 rounded-md" /> : null}
      {dry.data ? <Result result={dry.data} definition={definition} /> : null}
    </div>
  );
}
